import React, { useEffect, useRef } from 'react';

const DEFAULT_WIDTH_CLASS = 'w-2/5';

interface SlideOverModalLayoutProps {
    children: React.ReactNode;
    widthClass?: string;
    onClose?: () => void;
}

const SlideOverModalLayout: React.FC<SlideOverModalLayoutProps> = ({
    children,
    widthClass = DEFAULT_WIDTH_CLASS,
    onClose
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!onClose) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
        if (!modalRef.current) return;

        const modal = modalRef.current;
        const FOCUSABLE_SELECTOR =
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        const getFocusableElements = () => modal.querySelectorAll(FOCUSABLE_SELECTOR);

        // M3 fix: save previously focused element and restore on unmount
        const previouslyFocused = document.activeElement as HTMLElement | null;

        // Focus first element on mount
        const initial = getFocusableElements();
        (initial[0] as HTMLElement)?.focus();

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            const focusable = getFocusableElements();
            if (focusable.length === 0) return;
            const first = focusable[0] as HTMLElement;
            const last = focusable[focusable.length - 1] as HTMLElement;

            if (e.shiftKey) {
                if (document.activeElement === first) {
                    e.preventDefault();
                    last?.focus();
                }
            } else {
                if (document.activeElement === last) {
                    e.preventDefault();
                    first?.focus();
                }
            }
        };

        modal.addEventListener('keydown', handleTab as EventListener);
        return () => {
            modal.removeEventListener('keydown', handleTab as EventListener);
            previouslyFocused?.focus();
        };
    }, []);

    const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && onClose) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-end bg-black bg-opacity-50"
            onMouseDown={handleBackdropMouseDown}
        >
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                className={`relative bg-white h-full flex flex-col shadow-xl ${widthClass}`}
            >
                {children}
            </div>
        </div>
    );
};

export default SlideOverModalLayout;
